/**
 * Feature hook: IFS browse-file auto-population (Working Document check-in).
 *
 * When a file is browsed into the check-in flow and its name follows the IFS
 * document naming convention, this hook loads the matching document details
 * (DocNo / Revision) and pre-fills the check-in form:
 *   - document class
 *   - format size (matched against the class' available formats)
 *   - object connections (when provided by the document details)
 *
 * If the filename is not an IFS name, or validation fails, the existing
 * manual check-in flow is preserved untouched.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import store from "../store/store";
import { DATA_LOAD_STATUS } from "../constants/metaData";
import UserAuth from "../../auth/userAuth";
import { getDocumentDetails, getDocumentNewRevisionInfo } from "../api/documentsAPI";
import { normalizeDocumentRevisionInfoResponse } from "../../utils/normalizeDocumentRevisionInfoResponse";
import { parseIFSFileName } from "../../functions/ifsFileName";
import { useCheckin } from "./useCheckin";
import { useTools } from "./useTools";
import { logger } from "../../utils/logger";

function extractFormatSize(revision) {
  return revision?.FormatSize ?? revision?.formatSize ?? "";
}

function extractObjectConnections(revision) {
  const raw = revision?.ObjectConnections ?? revision?.objectConnections ?? [];
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }
  return raw.map((connection) => ({
    ...connection,
    objectID: connection?.objectID ?? connection?.ObjectID ?? "",
    objectType: connection?.objectType ?? connection?.ObjectType ?? "",
    objectNo: connection?.objectNo ?? connection?.ObjectNo ?? connection?.objectNumber ?? "",
    objectName: connection?.objectName ?? connection?.ObjectName ?? "",
  }));
}

function parseClassFormats(payload) {
  if (!payload) {
    return { formats: [], viewRequired: "N", formatRequired: "N", objConRequired: "N", restrictedAccess: "N" };
  }
  const classItems = Array.isArray(payload)
    ? payload
    : payload?.classDetails ??
      payload?.ClassDetails ??
      payload?.value?.classDetails ??
      payload?.value?.ClassDetails ??
      [];
  if (!Array.isArray(classItems) || classItems.length === 0) {
    return { formats: [], viewRequired: "N", formatRequired: "N", objConRequired: "N", restrictedAccess: "N" };
  }
  const classItem = classItems[0];
  const formats = classItem?.formats ?? classItem?.Formats ?? [];
  return {
    formats: Array.isArray(formats) ? formats : [],
    viewRequired: `${classItem?.viewRequired ?? classItem?.ViewRequired ?? "N"}`,
    formatRequired: `${classItem?.formatMandatory ?? classItem?.FormatMandatory ?? "N"}`,
    objConRequired: `${classItem?.objectConnectionRequired ?? classItem?.ObjectConnectionRequired ?? "N"}`,
    restrictedAccess: `${classItem?.restrictedAccess ?? classItem?.RestrictedAccess ?? "N"}`,
  };
}

function formatMatches(formatItem, formatSize) {
  if (!formatSize) {
    return false;
  }
  const candidate = formatItem?.formatSize ?? formatItem?.FormatSize;
  return String(candidate) === String(formatSize);
}

/**
 * Resolve IFS identifiers for the selected check-in document.
 *
 * Two supported sources, mirroring how the document reaches the flow:
 *  - Browse files (local): IFS identity is parsed from the file name.
 *  - Channel/OneDrive documents that are already checked in: the list/API call
 *    carries the IFS metadata (document number, class, rev, sheet, format size),
 *    so the identity is read straight from the document metadata instead of
 *    parsing the file name.
 *
 * Returns null when the document carries no IFS identity (e.g. a brand-new file
 * that has never been checked in), so the manual flow / channel-config defaults
 * (Addovation Share - Configuration) are kept untouched.
 */
function resolveSelectedDocumentIdentifiers(doc, fileName) {
  if (doc?.source === "browse") {
    const parsed = parseIFSFileName(fileName);
    if (!parsed || !parsed.isIFSFormat || !parsed.docClass || !parsed.docNo || !parsed.docRev) {
      return null;
    }
    return parsed;
  }

  const docClass = doc?.docClass ?? doc?.DocClass ?? "";
  const docNo = doc?.docNo ?? doc?.DocNo ?? "";
  const docRev = doc?.docRev ?? doc?.DocRev ?? "";
  if (!docClass || !docNo || !docRev) {
    return null;
  }
  return {
    docClass,
    docNo,
    docSheet: doc?.docSheet ?? doc?.DocSheet ?? "1",
    docRev,
    formatSize: doc?.formatSize ?? doc?.FormatSize ?? "",
    isCheckedIn: true,
  };
}

/**
 * The check-in form loads channel-config default object connections (get-logicalunits)
 * on mount. That response can arrive after the document connections we set, overwriting
 * them. This helper re-asserts the document connections once the config load settles so
 * the document connections always win, then stops so later user edits are preserved.
 * @param {Array<any>} connections
 * @param {(list: Array<any>) => void} apply - applies the connections to Redux
 * @returns {() => void} cleanup function
 */
export function guardObjectConnectionsAgainstConfigOverwrite(connections, apply) {
  let configSettled = false;
  let unsubscribe = () => {};
  const timeoutId = setTimeout(() => {
    if (configSettled) return;
    configSettled = true;
    unsubscribe();
    logger.debug(
      "Object connection config load did not settle in time - keeping document connections",
      null,
      "CHECKIN",
    );
  }, 20000);

  unsubscribe = store.subscribe(() => {
    if (configSettled) return;
    const status = store.getState()?.Metadata?.objectConnectionDataLoad;
    if (
      status === DATA_LOAD_STATUS.onStart ||
      status === DATA_LOAD_STATUS.onLoad
    ) {
      return;
    }
    configSettled = true;
    clearTimeout(timeoutId);
    unsubscribe();
    logger.info(
      "Object connection config load settled - re-asserting document connections",
      null,
      "CHECKIN",
    );
    apply(connections);
  });

  return () => {
    if (!configSettled) {
      configSettled = true;
      clearTimeout(timeoutId);
      unsubscribe();
    }
  };
}

export function useIFSBrowseAutopopulation() {
  const location = useLocation();
  const selectedDocument = location.state?.selectedDocument ?? null;
  const processedKeyRef = useRef(null);
  const connectionGuardCleanupRef = useRef(null);
  const [isAutopopulating, setIsAutopopulating] = useState(false);
  const [isAutopopulated, setIsAutopopulated] = useState(false);

  const { uploadFiles, uploadViewCopyCount, onGetFormatsByClass } = useCheckin();
  const {
    onDocClassFormatChange,
    onSelectedObjectConnectionChanged,
    onRecentObjectConnectionChanged,
  } = useTools();

  useEffect(() => {
    const browseFile = selectedDocument;
    if (!browseFile) {
      return;
    }

    const fileName = browseFile.name ?? "";
    const fileKey = browseFile.key ?? browseFile.id ?? fileName;
    if (!fileName || processedKeyRef.current === fileKey) {
      return;
    }

    const parsed = resolveSelectedDocumentIdentifiers(browseFile, fileName);
    if (!parsed) {
      logger.debug(
        "IFS identifiers not resolved - keeping existing flow",
        { fileName, source: browseFile.source ?? "" },
        "CHECKIN",
      );
      return;
    }

    logger.info(
      browseFile.source === "browse"
        ? "Browse file selected"
        : "Checked-in document selected",
      { fileName, source: browseFile.source ?? "" },
      "CHECKIN",
    );

    processedKeyRef.current = fileKey;
    setIsAutopopulating(true);
    setIsAutopopulated(false);

    logger.info(
      browseFile.source === "browse"
        ? "IFS filename detected / validation successful"
        : "Checked-in document IFS metadata detected",
      {
        docClass: parsed.docClass,
        docNo: parsed.docNo,
        docSheet: parsed.docSheet,
        docRev: parsed.docRev,
        formatSize: parsed.formatSize || "Not defined",
        fileExtension: parsed.fileExtension,
        title: parsed.title,
      },
      "CHECKIN",
    );

    const run = async () => {
      try {
        const tokenResponse = await new UserAuth().getUserToken(true);
        const accessToken = tokenResponse?.accessToken ?? null;
        if (!accessToken) {
          logger.warn("IFS browse-file auto-population skipped: no access token", null, "CHECKIN");
          return;
        }

        logger.info(browseFile.source === "browse" ? "Loading document details for browsed file" : "Loading document details for checked-in file", {
          docNo: parsed.docNo,
          docRev: parsed.docRev,
        }, "CHECKIN");
        const { data, status } = await getDocumentDetails(
          {
            DocClass: parsed.docClass,
            DocNo: parsed.docNo,
            DocSheet: parsed.docSheet,
            DocRev: parsed.docRev,
          },
          accessToken
        );

        if (status !== 200) {
          logger.warn(browseFile.source === "browse" ? "Failed to load document details for browsed file" : "Failed to load document details for checked-in file", { status }, "CHECKIN");
          return;
        }

        const { revision } = normalizeDocumentRevisionInfoResponse(data);
        const formatSize = extractFormatSize(revision) || parsed.formatSize || "";
        logger.info(browseFile.source === "browse" ? "Document details loaded for browsed file" : "Document details loaded for checked-in file", {
          formatSize: formatSize || "Not defined",
          isViewCopyExists: data?.isViewCopyExists,
        }, "CHECKIN");

        logger.info("Loading formats for document class", { docClass: parsed.docClass }, "CHECKIN");
        let classRules = { formats: [], viewRequired: "N", formatRequired: "N", objConRequired: "N", restrictedAccess: "N" };
        try {
          const payload = await onGetFormatsByClass(parsed.docClass);
          if (!payload) {
            logger.warn("Failed to load formats for document class", { docClass: parsed.docClass }, "CHECKIN");
          } else {
            classRules = parseClassFormats(payload);
          }
        } catch (err) {
          logger.warn("Failed to load formats for document class", err, "CHECKIN");
        }

        const hasMatchingFormat =
          formatSize && classRules.formats.some((item) => formatMatches(item, formatSize));
        const selectedFormat = hasMatchingFormat ? formatSize : "";
        logger.info(hasMatchingFormat ? "Format selected from FormatSizeRef" : "Failed to match FormatSizeRef with available formats", {
          formatSize: formatSize || "Not defined",
          selectedFormat: selectedFormat || "Not selected",
          availableFormats: classRules.formats.map((item) => item?.formatSize ?? item?.FormatSize),
        }, "CHECKIN");

        onDocClassFormatChange(
          parsed.docClass,
          selectedFormat,
          classRules.viewRequired,
          classRules.formatRequired,
          classRules.objConRequired,
          uploadFiles,
          uploadViewCopyCount,
          { restrictedAccess: classRules.restrictedAccess }
        );

        let connections = extractObjectConnections(revision);
        if (connections.length === 0) {
          logger.info(browseFile.source === "browse" ? "Loading object connections for browsed file" : "Loading object connections for checked-in file", {
            docNo: parsed.docNo,
            docRev: parsed.docRev,
          }, "CHECKIN");
          try {
            const newRevResponse = await getDocumentNewRevisionInfo(
              {
                docClass: parsed.docClass,
                docNo: parsed.docNo,
                docSheet: parsed.docSheet,
                docRev: parsed.docRev,
                requestDocRevInfo: false,
              },
              accessToken
            );
            const objectConnections =
              newRevResponse?.data?.newRevisionBasicInfo?.objectConnections ??
              newRevResponse?.data?.NewRevisionBasicInfo?.objectConnections ??
              [];
            connections = Array.isArray(objectConnections)
              ? objectConnections.filter(
                  (item) => item?.luName && item?.keyRef
                )
              : [];
          } catch (err) {
            logger.warn(browseFile.source === "browse" ? "Failed to load object connections for browsed file" : "Failed to load object connections for checked-in file", err, "CHECKIN");
          }
        }

        if (connections.length > 0) {
          logger.info(browseFile.source === "browse" ? "Setting object connections from browsed file document details" : "Setting object connections from checked-in file document details", {
            connectionCount: connections.length,
          }, "CHECKIN");
          onRecentObjectConnectionChanged(connections);
          onSelectedObjectConnectionChanged(connections, []);
          if (connectionGuardCleanupRef.current) {
            connectionGuardCleanupRef.current();
            connectionGuardCleanupRef.current = null;
          }
          connectionGuardCleanupRef.current =
            guardObjectConnectionsAgainstConfigOverwrite(connections, (list) => {
              if (processedKeyRef.current !== fileKey) return;
              onRecentObjectConnectionChanged(list);
              onSelectedObjectConnectionChanged(list, []);
            });
        } else {
          logger.info(browseFile.source === "browse" ? "No object connections found for browsed file - keeping existing flow" : "No object connections found for checked-in file - keeping existing flow", {
            documentKey: browseFile.key ?? browseFile.id,
          }, "CHECKIN");
        }

        setIsAutopopulated(true);

        logger.info("IFS browse-file auto-population completed", {
          docClass: parsed.docClass,
          formatSize: selectedFormat || "Not selected",
          objectConnections: connections.length,
        }, "CHECKIN");
      } catch (err) {
        logger.error(browseFile.source === "browse" ? "Failed to auto-populate check-in from browsed IFS file" : "Failed to auto-populate check-in from checked-in file", err, "CHECKIN");
      } finally {
        setIsAutopopulating(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocument]);

  useEffect(
    () => () => {
      if (connectionGuardCleanupRef.current) {
        connectionGuardCleanupRef.current();
        connectionGuardCleanupRef.current = null;
      }
    },
    [],
  );

  return { isAutopopulating, isAutopopulated };
}
